interface PromiseWithHandlers {
  promise: Promise<void>;
  resolve: () => void;
  reject: (err: unknown) => void;
}

/**
 * SyncManager does the following:
 * - Makes sure that there are no parallel syncs runnning.
 * - Queues syncs that are called while a sync is already running.
 * - Calls sync only once even if there are multiple queued syncs.
 * - Returns a promise that resolves when the sync is complete.
 */
export class SyncManager {
  constructor(syncFunc: () => Promise<void>) {
    this.syncFunc = syncFunc;
  }

  private syncFunc: () => Promise<void>;
  private currentSync: PromiseWithHandlers | null = null;
  private nextSync: PromiseWithHandlers | null = null;

  sync = () => {
    if (!this.nextSync) {
      this.nextSync = this.createPromiseWithHandlers();
    }

    const promiseToReturn = this.nextSync.promise;

    if (!this.currentSync) {
      this.currentSync = this.nextSync;
      this.nextSync = null;
      this.runSyncLoop();
    }

    return promiseToReturn;
  };

  private runSyncLoop = async () => {
    while (this.currentSync) {
      try {
        await this.syncFunc();
        this.currentSync.resolve();
      } catch (error) {
        this.currentSync.reject(error);
      } finally {
        this.currentSync = this.nextSync;
        this.nextSync = null;
      }
    }
  };

  private createPromiseWithHandlers(): PromiseWithHandlers {
    let resolve: () => void;
    let reject: (err: unknown) => void;

    const promise = new Promise<void>((_resolve, _reject) => {
      resolve = _resolve;
      reject = _reject;
    });

    return {
      promise,
      resolve: resolve!,
      reject: reject!,
    };
  }
}
